import { useTranslation } from 'react-i18next'
import type { ReactElement } from 'react'
import { StoreLinks } from '../../../scripts/storeLinks.mjs'
import { storeCtaHref } from '../storeCtaHref'
import { reportStoreOutboundTapped } from '../../analytics/storeOutboundEvents'
import { realMetroAnalyticsReporter } from '../../analytics/webAnalyticsReporter'
import type { MetroAnalyticsReporter } from '../../interaction/metroAnalyticsReporter'
import badgeAppStore from './assets/badge-app-store.svg'
import badgeGooglePlay from './assets/badge-google-play.svg'
import iconMap from './assets/promo-icon-map.png'
import iconMoon from './assets/promo-icon-moon.png'
import iconTrain from './assets/promo-icon-train.png'
import styles from './StationAppPromo.module.css'

const FEATURES = [
  { icon: iconMap, titleKey: 'promo_feature_map_title', textKey: 'promo_feature_map_text' },
  { icon: iconMoon, titleKey: 'promo_feature_night_title', textKey: 'promo_feature_night_text' },
  {
    icon: iconTrain,
    titleKey: 'promo_feature_carriage_title',
    textKey: 'promo_feature_carriage_text',
  },
] as const

/**
 * App-promo banner — Figma 8597:44719 (TASK-699), the page's closing
 * conversion block. The orange gradient background (blob vectors +
 * glass blur) is a single Figma-exported JPEG — the layer stack is
 * container-query math that cannot be reproduced in CSS; the iPhone
 * frame IS reproduced in CSS (black borders + Dynamic Island) around
 * the exported app screenshot, so it stays crisp at every scale.
 * Store badges carry the TASK-644 web-station-page campaign slot; the
 * badge of the visitor's own phone store goes through the city's
 * `/go/site-station-page` tracking link instead ({@link storeCtaHref},
 * TASK-1170). `rel="nofollow"` keeps crawlers out of the click counts.
 */
export function StationAppPromo({
  reporter = realMetroAnalyticsReporter,
}: {
  /** Analytics sink. Optional — defaults to {@link realMetroAnalyticsReporter}. */
  reporter?: MetroAnalyticsReporter
} = {}): ReactElement {
  const { t } = useTranslation('station-page')
  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <div className={styles.copy}>
          <span className={styles.pill}>{t('promo_pill')}</span>
          <h2 className={styles.heading}>
            {t('promo_heading_line1')}
            <br />
            {t('promo_heading_line2')} <span className={styles.headingAccent}>Tube Map</span>
          </h2>
          <p className={styles.subtitle}>{t('promo_subtitle')}</p>
          {/* Official store badges (Apple marketing / Google Play brand
              assets, self-hosted SVGs) — the design's hand-drawn emoji
              buttons were replaced per review 2026-08-04. */}
          <div className={styles.badges}>
            <a
              className={styles.badge}
              href={storeCtaHref(navigator.userAgent, 'app_store', StoreLinks.SLOTS.STATION_PAGE)}
              target="_blank"
              rel="nofollow noreferrer"
              onClick={() =>
                reportStoreOutboundTapped(reporter, {
                  store: 'app_store',
                  slot: StoreLinks.SLOTS.STATION_PAGE,
                })
              }
            >
              <img
                src={badgeAppStore}
                alt={t('promo_badge_app_store_alt')}
                className={styles.badgeImage}
              />
            </a>
            <a
              className={styles.badge}
              href={storeCtaHref(navigator.userAgent, 'google_play', StoreLinks.SLOTS.STATION_PAGE)}
              target="_blank"
              rel="nofollow noreferrer"
              onClick={() =>
                reportStoreOutboundTapped(reporter, {
                  store: 'google_play',
                  slot: StoreLinks.SLOTS.STATION_PAGE,
                })
              }
            >
              <img
                src={badgeGooglePlay}
                alt={t('promo_badge_google_play_alt')}
                className={styles.badgeImage}
              />
            </a>
          </div>
          <ul className={styles.features}>
            {FEATURES.map(({ icon, titleKey, textKey }) => (
              <li key={titleKey} className={styles.feature}>
                <img src={icon} alt="" width={44} height={44} className={styles.featureIcon} />
                <span className={styles.featureTitle}>{t(titleKey)}</span>
                <span className={styles.featureText}>{t(textKey)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.phoneColumn}>
          <div className={styles.phone}>
            <img
              src="/promo/station-promo-app-screen.jpg"
              alt={t('promo_phone_alt')}
              className={styles.phoneScreen}
            />
            <span className={styles.dynamicIsland} aria-hidden />
          </div>
        </div>
      </div>
    </div>
  )
}
